/** My progress: course progress, assignments, assessments, certificates and trainer feedback. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html, safeUrl } from '../../core/html.js';
import { fmtDate, fmtDateTime } from '../../core/format.js';
import { currentView, reload, update } from '../../app/shell.js';
import { backLink, banner, bar, empty, ic, seg, table, tag } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { filePicker, openFile, uploadChosen } from '../../ui/upload.js';

const TABS = [['courses', 'Courses'], ['assignments', 'Assignments'], ['assessments', 'Assessments'], ['certificates', 'Certificates'], ['feedback', 'Feedback']];

function assignmentStatus(a) {
  const s = a.submission;
  if (s?.graded_at) return tag(`Graded ${s.score}/${a.max_score}`, 'ok');
  if (s) return tag(s.late ? 'Submitted late' : 'Submitted', 'ok');
  return a.overdue ? tag('Overdue', 'bad') : tag('Open', 'wn');
}

const progress = {
  path: '/student/progress',
  roles: ['student'],
  title: 'My progress',
  ui: (ctx) => ({ tab: TABS.some(([k]) => k === ctx.query.get('tab')) ? ctx.query.get('tab') : 'courses' }),
  async load(ctx) {
    const signal = ctx.signal;
    const [mine, assignments, assessments, certificates, feedback] = await Promise.all([
      api.get('/me/enrollments', { signal }),
      api.get('/student/assignments', { signal }),
      api.get('/student/assessments', { signal }),
      api.get('/student/certificates', { signal }),
      api.get('/student/feedback', { signal }),
    ]);
    return { enrollments: mine.enrollments, assignments: assignments.assignments, assessments: assessments.assessments, certificates: certificates.certificates, feedback: feedback.feedback };
  },
  view(d, ui) {
    const counts = { assignments: d.assignments.filter((a) => !a.submission).length, certificates: d.certificates.length };
    const tabs = TABS.map(([k, label]) => [k, counts[k] ? `${label} (${counts[k]})` : label]);
    let body;
    if (ui.tab === 'courses') {
      body = d.enrollments.length
        ? html`<div class="grid g2">${d.enrollments.map((e) => html`<div class="card"><div class="row between"><h3 style="margin:0">${e.course.title}</h3><b>${e.progress.percent}%</b></div>${bar(e.progress.percent, '', e.course.title)}
            <p class="sm mu mt-s" style="margin:0">${e.progress.classes_done} of ${e.progress.classes_total} classes. ${e.progress.assignments_done} of ${e.progress.assignments_total} assignments. ${e.progress.has_final ? (e.progress.final_passed ? 'Final passed.' : 'Final assessment pending.') : ''}</p></div>`)}</div>`
        : empty('chart', 'Nothing to track yet', 'Join a course and your progress shows up here.', html`<a class="btn" href="/student/learn">Find a course</a>`);
    } else if (ui.tab === 'assignments') {
      body = table(['Assignment', 'Due', 'Status', ''], d.assignments.map((a) => [a.title, fmtDateTime(a.due_at), assignmentStatus(a), html`<a class="btn ${a.submission ? 'ghost' : ''} sm" href="/student/assignments/${a.id}">${a.submission ? 'View' : 'Open'}</a>`]), { empty: 'No assignments yet.' });
    } else if (ui.tab === 'assessments') {
      body = table(['Test', 'Questions', 'Best score', 'Result', ''], d.assessments.map((a) => [
        a.title, a.question_count, a.best_percent == null ? 'Not taken' : `${a.best_percent}%`,
        a.best_percent == null ? tag(a.kind === 'final' ? 'Final' : 'Open', 'wn') : a.best_percent >= a.pass_percent ? tag('Passed', 'ok') : tag('Retake needed', 'wn'),
        html`<a class="btn sm ${a.best_percent == null ? '' : 'ghost'}" href="/student/assessments/${a.id}">${a.best_percent == null ? 'Start' : 'Retake'}</a>`,
      ]), { empty: 'No assessments yet.' });
    } else if (ui.tab === 'certificates') {
      body = d.certificates.length
        ? html`<div class="grid g2">${d.certificates.map((c) => html`<div class="cert-card card">${ic('award')}<h3 class="mt-s">${c.course}</h3><p class="sm mu">Score ${c.score_percent}%. Trainer ${c.trainer}. Issued ${fmtDate(c.issued_at)}.</p><p class="xs mu">ID <span class="mono">${c.code}</span></p>
            <div class="row"><a class="btn sm" href="/api/v1/public/certificates/${c.code}/print" target="_blank" rel="noopener" data-native>${ic('dl')} Open certificate</a>
              <button type="button" class="btn ghost sm" data-act="cert.copy" data-code="${c.code}">${ic('link')} Copy check link</button>
              <button type="button" class="btn ghost sm" data-act="cert.visibility" data-id="${c.id}" data-show="${!c.show_on_profile}">${c.show_on_profile ? 'Hide from profile' : 'Show on profile'}</button></div></div>`)}</div>`
        : empty('award', 'No certificate yet', 'Pass the final assessment of a course to earn one.');
    } else {
      body = d.feedback.length
        ? html`<div class="list">${d.feedback.map((f) => html`<div class="li"><span class="f1"><b>${f.trainer}</b><br>${f.text}</span><span class="xs mu">${fmtDate(f.at)}</span></div>`)}</div>`
        : empty('chat', 'No feedback yet', 'Trainer feedback appears here.');
    }
    return html`${seg(tabs, ui.tab, 'progress.tab', 'Progress sections')}<div class="mt">${body}</div>`;
  },
};

register({
  'progress.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),
  'cert.copy': async (el) => {
    const link = `${location.origin}/verify/${el.dataset.code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast('Link copied', 'link');
    } catch {
      toast(link, 'link');
    }
  },
  'cert.visibility': async (el) => {
    await api.put(`/student/certificates/${el.dataset.id}/visibility`, { body: { show_on_profile: el.dataset.show === 'true' } });
    await reload();
  },
});

/* ---------- one assignment ---------- */

const assignment = {
  path: '/student/assignments/:id',
  roles: ['student'],
  nav: 'progress',
  title: (d) => d?.assignment?.title || 'Assignment',
  ui: () => ({ error: '' }),
  load: (ctx) => api.get(`/student/assignments/${ctx.params.id}`, { signal: ctx.signal }),
  view(d, ui) {
    const a = d.assignment;
    const s = a.submission;
    return html`${backLink('/student/progress?tab=assignments', 'My progress')}<div class="card" style="max-width:760px">
      <div class="row between"><h2 style="margin:0">${a.title}</h2>${s ? assignmentStatus({ ...a }) : tag(`Due ${fmtDateTime(a.due_at)}`, new Date(a.due_at) < new Date() ? 'bad' : 'wn')}</div>
      <p class="mu mt-s" style="white-space:pre-wrap">${a.description}</p>${a.link ? html`<p class="sm"><a href="${safeUrl(a.link)}" target="_blank" rel="noopener noreferrer">${ic('link')} Open the link your trainer shared</a></p>` : ''}<p class="xs mu">Worth ${a.max_score} points. Due ${fmtDateTime(a.due_at)}.</p>
      ${s ? html`<div class="divider"></div><h3>Your submission</h3><p class="xs mu">Sent ${fmtDateTime(s.submitted_at)}${s.late ? ' (late)' : ''}</p>
        ${s.note ? html`<p style="white-space:pre-wrap">${s.note}</p>` : ''}${s.file_id ? html`<button type="button" class="btn ghost sm" data-act="file.open" data-id="${s.file_id}">${ic('file')} Open your file</button>` : ''}${s.link ? html`<p class="sm"><a href="${safeUrl(s.link)}" target="_blank" rel="noopener noreferrer">${ic('link')} ${s.link}</a></p>` : ''}
        ${s.graded_at ? banner(html`<b>${s.score} of ${a.max_score}</b>${s.feedback ? html`. ${s.feedback}` : ''}`, 'good', 'award') : banner('Waiting for your trainer to grade it.', '', 'info')}`
        : html`<div class="divider"></div><form data-submit="assignment.submit" data-id="${a.id}" novalidate>
          ${field('as-note', 'Notes for your trainer', { name: 'note', rows: 5, req: false, max: 5000 })}${filePicker('as-file', 'Attach your work', 'submission')}
          ${field('as-link', 'Or share it as a link', { name: 'link', type: 'url', req: false, maxlength: 500, ph: 'https://github.com/you/project or a Drive link', help: 'Make sure the link opens for anyone who has it.' })}
          ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}<button class="btn" type="submit">${ic('upload')} Submit</button></form>`}</div>`;
  },
};

register({
  'file.open': (el) => openFile(el.dataset.id),
  'assignment.submit': (form) => submitForm(
    form,
    async ({ note, link }) => {
      const stored = await uploadChosen(form.querySelector('input[type=file]'), 'submission');
      if (!note && !stored && !link) throw new Error('Add a note, attach a file or share a link.');
      await api.post(`/student/assignments/${form.dataset.id}/submit`, { body: { note, file_id: stored?.id, link: link || '' } });
      toast('Submitted');
      await reload();
    },
    { onError: (error) => update((ui) => { ui.error = error.message; }) },
  ),
});

/* ---------- take an assessment ---------- */

const assessment = {
  path: '/student/assessments/:id',
  roles: ['student'],
  nav: 'progress',
  title: (d) => d?.assessment?.title || 'Assessment',
  ui: () => ({ answers: [], result: null, error: '' }),
  load: (ctx) => api.get(`/student/assessments/${ctx.params.id}`, { signal: ctx.signal }),
  view(d, ui) {
    const a = d.assessment;
    if (ui.result) {
      const r = ui.result;
      return html`${backLink('/student/progress?tab=assessments', 'My progress')}<div class="card" style="max-width:640px">
        ${r.passed ? banner(html`<b>You passed with ${r.percent}%</b> (${r.score} of ${r.total}).${r.certificate_code ? ' Your certificate is ready.' : ''}`, 'good', 'award') : banner(html`<b>You scored ${r.percent}%.</b> You need ${r.pass_percent}% to pass. Review your notes and try again.`, 'wn', 'alert')}
        <div class="row mt">${r.certificate_code ? html`<a class="btn" href="/student/progress?tab=certificates">${ic('award')} View certificate</a>` : ''}<button type="button" class="btn ghost" data-act="assessment.retry">Retake</button></div></div>`;
    }
    return html`${backLink('/student/progress?tab=assessments', 'My progress')}<div class="card" style="max-width:760px">
      <div class="row between"><h2 style="margin:0">${a.title}</h2>${tag(`${a.question_count} questions`)}</div><p class="mu">Pass mark ${a.pass_percent}%. Answer every question.</p>
      <form data-submit="assessment.submit" data-id="${a.id}" novalidate>${a.questions.map((q, i) => html`<fieldset class="quiz-q"><legend><b>${i + 1}. ${q.q}</b></legend>
        <div class="row mt-s">${q.options.map((o, j) => html`<label class="opt" style="margin:0"><input type="radio" name="q${i}" value="${j}" ${ui.answers[i] === j ? 'checked' : ''} data-change="assessment.pick" data-q="${i}"> ${o}</label>`)}</div></fieldset>`)}
        ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}<button class="btn mt-s" type="submit">Submit assessment</button></form></div>`;
  },
};

register({
  // Remember the choice without redrawing the page.
  'assessment.pick': (el) => { currentAnswers().answers[Number(el.dataset.q)] = Number(el.value); },
  'assessment.retry': () => update((ui) => { ui.result = null; ui.answers = []; ui.error = ''; }),
  'assessment.submit': (form) => submitForm(
    form,
    async () => {
      const { answers, total } = currentAnswers();
      if (answers.filter((x) => Number.isInteger(x)).length < total) throw new Error('Answer every question before submitting.');
      const { result } = await api.post(`/student/assessments/${form.dataset.id}/attempt`, { body: { answers: Array.from({ length: total }, (_, i) => answers[i]) } });
      update((ui) => { ui.result = result; ui.error = ''; });
      window.scrollTo(0, 0);
    },
    { onError: (error) => update((ui) => { ui.error = error.message; }) },
  ),
});

function currentAnswers() {
  const v = currentView();
  return { answers: v.ui.answers, total: v.data.assessment.questions.length };
}

/* ---------- old notification links ---------- */

const to = (path) => ({ guard: () => path });
const redirects = [
  { path: '/student/certificates', roles: ['student'], ...to('/student/progress?tab=certificates') },
  { path: '/student/feedback', roles: ['student'], ...to('/student/progress?tab=feedback') },
  { path: '/student/assignments', roles: ['student'], ...to('/student/progress?tab=assignments') },
];

export const routes = [progress, assignment, assessment, ...redirects];
